import { API_BASE } from '../../config/apiBase';
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { 
  ArrowLeft, FileText, Download, RefreshCw, Printer, 
  BookOpen, Trophy, CheckCircle, AlertCircle, Eye,
  FileDown, Lock, Sparkles, ClipboardList, Play, Monitor,
  History, XCircle, Calendar, User, Clock
} from 'lucide-react';
import { toast } from 'sonner';

const API = API_BASE;

// Authorized admin email
const AUTHORIZED_ADMIN = 'credit@inspiredfunding.org';

export default function AssessmentBuilder() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [modules, setModules] = useState([]);
  const [finalExam, setFinalExam] = useState(null);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState({});
  const [downloading, setDownloading] = useState({});

  const lang = user?.training_language || 'es';
  const t = (en, es) => lang === 'es' ? es : en;

  // Check if user is authorized admin
  const isAuthorized = user?.role === 'admin' && user?.email === AUTHORIZED_ADMIN;

  useEffect(() => {
    if (!isAuthorized) {
      toast.error(t('Access denied', 'Acceso denegado'));
      navigate('/training');
      return;
    }
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [modulesRes, examRes, quizzesRes, resultsRes] = await Promise.all([
        axios.get(`${API}/training/modules`),
        axios.get(`${API}/training/admin/final-exam-status`).catch(() => ({ data: null })),
        axios.get(`${API}/training/admin/quizzes-status`).catch(() => ({ data: [] })),
        axios.get(`${API}/training/admin/assessment-results`).catch(() => ({ data: [] }))
      ]);
      
      // Merge quiz info into modules
      const quizzesMap = {};
      (quizzesRes.data || []).forEach(q => {
        if (!quizzesMap[q.module_id]) quizzesMap[q.module_id] = [];
        quizzesMap[q.module_id].push(q);
      });
      
      const modulesWithQuizzes = (modulesRes.data || []).map(m => ({
        ...m,
        quizzes: (quizzesMap[m.id] || []).sort((a, b) => (a.quiz_number || 1) - (b.quiz_number || 1)),
        quiz: quizzesMap[m.id]?.[0] || m.quiz // backward compat
      }));
      
      setModules(modulesWithQuizzes);
      setFinalExam(examRes.data);
      setResults(resultsRes.data || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error(t('Error loading data', 'Error al cargar datos'));
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateQuiz = async (moduleId) => {
    setGenerating(prev => ({ ...prev, [moduleId]: true }));
    try {
      const response = await axios.post(`${API}/training/admin/generate-quiz/${moduleId}`, {}, {
        timeout: 60000 // 60 second timeout
      });
      
      toast.success(t(
        `Quiz generated with ${response.data.questions_count} questions!`,
        `¡Quiz generado con ${response.data.questions_count} preguntas!`
      ));
      
      // Refresh data to show updated quiz
      await fetchData();
    } catch (error) {
      console.error('Error generating quiz:', error);
      const errorMsg = error.response?.data?.detail || error.message || t('Error generating quiz', 'Error al generar quiz');
      toast.error(errorMsg);
    } finally {
      setGenerating(prev => ({ ...prev, [moduleId]: false }));
    }
  };

  const handleGenerateCustomQuiz = async (moduleId, quizNumber, topicFocus, titleEs, titleEn) => {
    const genKey = `${moduleId}-q${quizNumber}`;
    setGenerating(prev => ({ ...prev, [genKey]: true }));
    try {
      toast.info(t(
        `Generating Quiz #${quizNumber}... This may take up to 1 minute.`,
        `Generando Quiz #${quizNumber}... Puede tomar hasta 1 minuto.`
      ));
      const response = await axios.post(`${API}/training/admin/generate-custom-quiz/${moduleId}`, {
        quiz_number: quizNumber,
        questions_count: 25,
        topic_focus: topicFocus,
        title_es: titleEs,
        title_en: titleEn
      }, { timeout: 120000 });
      
      toast.success(t(
        `Quiz #${quizNumber} generated with ${response.data.questions_count} questions!`,
        `¡Quiz #${quizNumber} generado con ${response.data.questions_count} preguntas!`
      ));
      await fetchData();
    } catch (error) {
      console.error('Error generating custom quiz:', error);
      const errorMsg = error.response?.data?.detail || error.message || t('Error generating quiz', 'Error al generar quiz');
      toast.error(errorMsg);
    } finally {
      setGenerating(prev => ({ ...prev, [genKey]: false }));
    }
  };

  const handleGenerateFinalExam = async () => {
    setGenerating(prev => ({ ...prev, finalExam: true }));
    try {
      toast.info(t('Generating final exam... This may take 1-2 minutes.', 'Generando examen final... Puede tomar 1-2 minutos.'));
      
      const response = await axios.post(`${API}/training/admin/regenerate-final-exam`, {}, {
        timeout: 180000 // 3 minute timeout for 150 questions
      });
      
      toast.success(t(
        `Final exam generated with ${response.data.questions_generated} questions!`,
        `¡Examen final generado con ${response.data.questions_generated} preguntas!`
      ));
      
      await fetchData();
    } catch (error) {
      console.error('Error generating final exam:', error);
      if (error.code === 'ECONNABORTED') {
        toast.error(t('Generation timed out. Please try again.', 'Tiempo agotado. Por favor intenta de nuevo.'));
      } else {
        toast.error(error.response?.data?.detail || t('Error generating exam', 'Error al generar examen'));
      }
    } finally {
      setGenerating(prev => ({ ...prev, finalExam: false }));
    }
  };

  const handleDownloadPDF = async (type, moduleId = null, withAnswers = false, quizNumber = 1) => {
    const key = moduleId ? `${moduleId}-q${quizNumber}-${withAnswers}` : `final-${withAnswers}`;
    setDownloading(prev => ({ ...prev, [key]: true }));
    
    try {
      const endpoint = type === 'quiz' 
        ? `${API}/training/admin/download-quiz/${moduleId}?with_answers=${withAnswers}&quiz_number=${quizNumber}`
        : `${API}/training/admin/download-final-exam?with_answers=${withAnswers}`;
      
      const response = await axios.get(endpoint, { 
        responseType: 'blob',
        timeout: 120000 // 2 minute timeout for large PDFs
      });
      
      // Create download link
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      const filename = type === 'quiz'
        ? `Quiz${quizNumber}_Modulo_${moduleId}${withAnswers ? '_ConRespuestas' : ''}.pdf`
        : `Examen_Final${withAnswers ? '_ConRespuestas' : ''}.pdf`;
      
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success(t('PDF downloaded!', '¡PDF descargado!'));
      
    } catch (error) {
      console.error('Error downloading PDF:', error);
      if (error.code === 'ECONNABORTED') {
        toast.error(t('Download timed out. Please try again.', 'Tiempo agotado. Por favor intenta de nuevo.'));
      } else {
        toast.error(error.response?.data?.detail || t('Error downloading PDF', 'Error al descargar PDF'));
      }
    } finally {
      setDownloading(prev => ({ ...prev, [key]: false }));
    }
  };

  // Download a previous result as PDF
  const handleDownloadResult = async (result) => {
    const key = `result-${result.id}`;
    setDownloading(prev => ({ ...prev, [key]: true }));
    
    try {
      const response = await axios.post(
        `${API}/training/admin/download-assessment-result`,
        {
          participantName: result.participant_name,
          assessmentType: result.assessment_type,
          assessmentId: result.assessment_id,
          assessmentTitle: result.assessment_title,
          totalQuestions: result.total_questions,
          correctAnswers: result.correct_answers,
          score: result.score,
          passed: result.passed,
          passingScore: result.passing_score,
          timeStarted: result.time_started,
          timeEnded: result.time_ended,
          timeTakenMinutes: result.time_taken_minutes,
          feedback: result.feedback || []
        },
        { responseType: 'blob', timeout: 60000 }
      );
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Resultado_${result.assessment_type === 'final' ? 'ExamenFinal' : 'Quiz'}_${result.participant_name?.replace(/\s+/g, '_')}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success(t('Results downloaded!', '¡Resultados descargados!'));
    } catch (error) {
      console.error('Error downloading result:', error);
      toast.error(t('Error downloading results', 'Error al descargar resultados'));
    } finally {
      setDownloading(prev => ({ ...prev, [key]: false }));
    }
  };

  // Format date for display
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('es-ES', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (!isAuthorized) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4 p-6">
        <Lock className="h-16 w-16 text-red-500" />
        <h2 className="text-2xl font-bold text-red-600">
          {t('Access Denied', 'Acceso Denegado')}
        </h2>
        <p className="text-muted-foreground text-center max-w-md">
          {t('Only authorized administrators can access this section.', 
             'Solo administradores autorizados pueden acceder a esta sección.')}
        </p>
        <Button onClick={() => navigate('/training')} className="mt-4">
          {t('Back to Training', 'Volver a Capacitación')}
        </Button>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="flex flex-col items-center gap-4">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          <p className="text-muted-foreground">{t('Loading assessments...', 'Cargando evaluaciones...')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-slate-900 to-slate-800 text-white px-6 py-8">
        <div className="max-w-6xl mx-auto">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/training')}
            className="text-white/70 hover:text-white hover:bg-white/10 mb-4"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            {t('Back to Training', 'Volver a Capacitación')}
          </Button>
          
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-xl bg-white/10 flex items-center justify-center">
              <ClipboardList className="h-7 w-7" />
            </div>
            <div>
              <h1 className="text-2xl md:text-3xl font-bold">
                {t('Assessment Builder', 'Generador de Evaluaciones')}
              </h1>
              <p className="text-white/60">
                {t('Generate, download, and print quizzes and exams', 
                   'Genera, descarga e imprime quizzes y exámenes')}
              </p>
            </div>
          </div>
          
          <div className="mt-4 flex items-center gap-2">
            <Badge className="bg-amber-500/20 text-amber-300 border-amber-500/30">
              <Lock className="h-3 w-3 mr-1" />
              {t('Admin Only', 'Solo Admin')}
            </Badge>
            <Badge className="bg-white/10 text-white/70 border-white/20">
              {user?.email}
            </Badge>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-6 py-8">
        <Tabs defaultValue="quizzes" className="space-y-6">
          <TabsList className="grid w-full max-w-lg grid-cols-3">
            <TabsTrigger value="quizzes" className="flex items-center gap-2">
              <BookOpen className="h-4 w-4" />
              {t('Quizzes', 'Quizzes')}
            </TabsTrigger>
            <TabsTrigger value="final" className="flex items-center gap-2">
              <Trophy className="h-4 w-4" />
              {t('Final Exam', 'Examen Final')}
            </TabsTrigger>
            <TabsTrigger value="results" className="flex items-center gap-2">
              <History className="h-4 w-4" />
              {t('Results', 'Resultados')}
              {results.length > 0 && (
                <Badge className="ml-1 bg-blue-100 text-blue-700 border-0 text-xs">{results.length}</Badge>
              )}
            </TabsTrigger>
          </TabsList>

          {/* Module Quizzes Tab */}
          <TabsContent value="quizzes" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BookOpen className="h-5 w-5 text-blue-600" />
                  {t('Module Quizzes', 'Quizzes por Módulo')}
                </CardTitle>
                <CardDescription>
                  {t('Each quiz contains 15 multiple-choice questions specific to the module content.',
                     'Cada quiz contiene 15 preguntas de opción múltiple específicas del contenido del módulo.')}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {modules.map((module) => {
                  const quizzes = module.quizzes || [];
                  const hasAnyQuiz = quizzes.some(q => q.questions_count > 0);
                  const isModule5 = Number(module.module_number) === 5;
                  const hasQuiz2 = quizzes.some(q => Number(q.quiz_number) === 2 && q.questions_count > 0);
                  
                  return (
                  <Card key={module.id} className={`border-slate-200 ${isModule5 ? 'ring-1 ring-amber-200' : ''}`}>
                    <CardContent className="p-4">
                      {/* Module header */}
                      <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 mb-3">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <Badge variant="outline" className="text-xs">
                              {t('Module', 'Módulo')} {module.module_number}
                            </Badge>
                            {hasAnyQuiz && (
                              <Badge className="bg-green-100 text-green-700 border-0 text-xs">
                                {quizzes.length} quiz{quizzes.length > 1 ? 'zes' : ''}
                              </Badge>
                            )}
                          </div>
                          <h3 className="font-semibold text-slate-900">
                            {lang === 'es' ? module.title_es : module.title_en}
                          </h3>
                          <p className="text-sm text-slate-500 mt-1">
                            {module.lessons_count} {t('lessons', 'lecciones')} • {module.estimated_hours}h
                          </p>
                        </div>
                        
                        <div className="flex flex-wrap gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleGenerateQuiz(module.id)}
                            disabled={generating[module.id]}
                            data-testid={`generate-quiz1-${module.id}`}
                          >
                            {generating[module.id] ? (
                              <RefreshCw className="h-4 w-4 mr-1 animate-spin" />
                            ) : (
                              <Sparkles className="h-4 w-4 mr-1" />
                            )}
                            {hasAnyQuiz ? t('Regenerate Quiz #1', 'Regenerar Quiz #1') : t('Generate Quiz #1', 'Generar Quiz #1')}
                          </Button>
                          {isModule5 && (
                            <Button
                              variant="outline"
                              size="sm"
                              className="border-amber-300 text-amber-700 hover:bg-amber-50"
                              onClick={() => handleGenerateCustomQuiz(
                                module.id,
                                2,
                                [
                                  'Fleet Trucks Listed on Camiondepot.com',
                                  'Programa de Financiamiento de Equipos Comerciales (EFA)',
                                  'Programa de Arrendamiento de Equipos Comerciales (ELA)',
                                  'Búsqueda de Equipo según Presupuesto'
                                ],
                                'Quiz #2 - Herramientas Técnicas y Portales',
                                'Quiz #2 - Technical Tools and Portals'
                              )}
                              disabled={generating[`${module.id}-q2`]}
                              data-testid={`generate-quiz2-${module.id}`}
                            >
                              {generating[`${module.id}-q2`] ? (
                                <RefreshCw className="h-4 w-4 mr-1 animate-spin" />
                              ) : (
                                <Sparkles className="h-4 w-4 mr-1" />
                              )}
                              {hasQuiz2
                                ? t('Regenerate Quiz #2', 'Regenerar Quiz #2') 
                                : t('Generate Quiz #2', 'Generar Quiz #2')}
                            </Button>
                          )}
                        </div>
                      </div>
                      
                      {/* Individual quizzes */}
                      {quizzes.map((quiz) => {
                        const qn = quiz.quiz_number || 1;
                        const dlKeyBlank = `${module.id}-q${qn}-false`;
                        const dlKeyAnswers = `${module.id}-q${qn}-true`;
                        
                        return (
                          <div key={quiz.id} className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 px-3 py-2.5 rounded-lg bg-slate-50 border border-slate-100 mb-2" data-testid={`quiz-row-${module.id}-${qn}`}>
                            <div className="flex items-center gap-2">
                              <Badge className={`${Number(qn) === 2 ? 'bg-amber-100 text-amber-700' : 'bg-blue-100 text-blue-700'} border-0 text-xs font-semibold`}>
                                Quiz #{qn}
                              </Badge>
                              <span className="text-sm text-slate-600">
                                {quiz.questions_count} {t('questions', 'preguntas')}
                              </span>
                              {quiz.topic_focus && quiz.topic_focus.length > 0 && (
                                <Badge variant="outline" className="text-xs text-slate-500">
                                  {quiz.topic_focus.length} {t('topics', 'temas')}
                                </Badge>
                              )}
                            </div>
                            <div className="flex flex-wrap gap-1.5">
                              <Button
                                variant="outline"
                                size="sm"
                                className="h-8 text-xs"
                                onClick={() => handleDownloadPDF('quiz', module.id, false, qn)}
                                disabled={downloading[dlKeyBlank]}
                                data-testid={`download-pdf-${module.id}-q${qn}`}
                              >
                                {downloading[dlKeyBlank] ? <RefreshCw className="h-3 w-3 mr-1 animate-spin" /> : <FileDown className="h-3 w-3 mr-1" />}
                                PDF
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                className="h-8 text-xs"
                                onClick={() => handleDownloadPDF('quiz', module.id, true, qn)}
                                disabled={downloading[dlKeyAnswers]}
                                data-testid={`download-answers-${module.id}-q${qn}`}
                              >
                                {downloading[dlKeyAnswers] ? <RefreshCw className="h-3 w-3 mr-1 animate-spin" /> : <FileDown className="h-3 w-3 mr-1" />}
                                {t('+ Answers', '+ Respuestas')}
                              </Button>
                              <Button
                                size="sm"
                                className="h-8 text-xs bg-green-600 hover:bg-green-700"
                                onClick={() => navigate(`/training/admin/take-assessment/quiz/${module.id}?quiz_number=${qn}`)}
                                data-testid={`take-online-${module.id}-q${qn}`}
                              >
                                <Monitor className="h-3 w-3 mr-1" />
                                {t('Take Online', 'Tomar en Línea')}
                              </Button>
                            </div>
                          </div>
                        );
                      })}
                      
                      {/* Module 5: Show prominent Quiz #2 section when not yet generated */}
                      {isModule5 && !hasQuiz2 && (
                        <div className="mt-3 p-4 rounded-lg border-2 border-dashed border-amber-300 bg-amber-50/50" data-testid="quiz2-pending-section">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                              <Sparkles className="h-5 w-5 text-amber-600" />
                            </div>
                            <div className="flex-1">
                              <p className="font-medium text-amber-900 text-sm">
                                Quiz #2 - {t('Technical Tools and Portals', 'Herramientas Técnicas y Portales')}
                              </p>
                              <p className="text-xs text-amber-700 mt-0.5">
                                {t(
                                  '25 technical questions on: Camiondepot.com, EFA, ELA, Budget Search',
                                  '25 preguntas técnicas sobre: Camiondepot.com, EFA, ELA, Búsqueda por Presupuesto'
                                )}
                              </p>
                            </div>
                            <Button
                              size="sm"
                              className="bg-amber-600 hover:bg-amber-700 text-white"
                              onClick={() => handleGenerateCustomQuiz(
                                module.id,
                                2,
                                [
                                  'Fleet Trucks Listed on Camiondepot.com',
                                  'Programa de Financiamiento de Equipos Comerciales (EFA)',
                                  'Programa de Arrendamiento de Equipos Comerciales (ELA)',
                                  'Búsqueda de Equipo según Presupuesto'
                                ],
                                'Quiz #2 - Herramientas Técnicas y Portales',
                                'Quiz #2 - Technical Tools and Portals'
                              )}
                              disabled={generating[`${module.id}-q2`]}
                              data-testid={`generate-quiz2-prominent-${module.id}`}
                            >
                              {generating[`${module.id}-q2`] ? (
                                <RefreshCw className="h-4 w-4 mr-1 animate-spin" />
                              ) : (
                                <Sparkles className="h-4 w-4 mr-1" />
                              )}
                              {t('Generate Now', 'Generar Ahora')}
                            </Button>
                          </div>
                        </div>
                      )}
                      
                      {/* Show empty state if no quizzes */}
                      {quizzes.length === 0 && (
                        <div className="text-center py-3 text-sm text-slate-400">
                          <AlertCircle className="h-4 w-4 inline mr-1" />
                          {t('No quiz generated yet', 'Aún no se ha generado quiz')}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                  );
                })}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Final Exam Tab */}
          <TabsContent value="final" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Trophy className="h-5 w-5 text-amber-600" />
                  {t('Final Certification Exam', 'Examen Final de Certificación')}
                </CardTitle>
                <CardDescription>
                  {t('100 unique questions covering all modules. Questions are different from module quizzes.',
                     '100 preguntas únicas cubriendo todos los módulos. Las preguntas son diferentes a las de los quizzes.')}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Exam Status */}
                <div className="bg-slate-50 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h3 className="font-semibold text-slate-900">
                        {t('Exam Status', 'Estado del Examen')}
                      </h3>
                      <p className="text-sm text-slate-500">
                        {finalExam?.questions_count 
                          ? `${finalExam.questions_count} ${t('questions generated', 'preguntas generadas')}`
                          : t('No exam generated yet', 'Aún no se ha generado el examen')}
                      </p>
                    </div>
                    {finalExam?.questions_count >= 100 ? (
                      <Badge className="bg-green-100 text-green-700 border-0">
                        <CheckCircle className="h-4 w-4 mr-1" />
                        {t('Ready', 'Listo')}
                      </Badge>
                    ) : (
                      <Badge className="bg-amber-100 text-amber-700 border-0">
                        <AlertCircle className="h-4 w-4 mr-1" />
                        {t('Incomplete', 'Incompleto')}
                      </Badge>
                    )}
                  </div>
                  
                  {finalExam?.questions_count > 0 && (
                    <Progress 
                      value={Math.min((finalExam.questions_count / 100) * 100, 100)} 
                      className="h-2"
                    />
                  )}
                </div>

                {/* Actions */}
                <div className="flex flex-wrap gap-3">
                  <Button
                    onClick={handleGenerateFinalExam}
                    disabled={generating.finalExam}
                    className="bg-amber-500 hover:bg-amber-600"
                  >
                    {generating.finalExam ? (
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Sparkles className="h-4 w-4 mr-2" />
                    )}
                    {finalExam?.questions_count 
                      ? t('Regenerate 100 Questions', 'Regenerar 100 Preguntas')
                      : t('Generate 100 Questions', 'Generar 100 Preguntas')}
                  </Button>
                  
                  {finalExam?.questions_count >= 50 && (
                    <>
                      <Button
                        variant="outline"
                        onClick={() => handleDownloadPDF('final', null, false)}
                        disabled={downloading['final-false']}
                      >
                        {downloading['final-false'] ? (
                          <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                        ) : (
                          <FileDown className="h-4 w-4 mr-2" />
                        )}
                        {t('Download PDF', 'Descargar PDF')}
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => handleDownloadPDF('final', null, true)}
                        disabled={downloading['final-true']}
                      >
                        {downloading['final-true'] ? (
                          <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                        ) : (
                          <FileDown className="h-4 w-4 mr-2" />
                        )}
                        {t('PDF + Answer Key', 'PDF + Clave de Respuestas')}
                      </Button>
                      <Button
                        className="bg-green-600 hover:bg-green-700"
                        onClick={() => navigate('/training/admin/take-assessment/final')}
                      >
                        <Monitor className="h-4 w-4 mr-2" />
                        {t('Take Online', 'Tomar en Línea')}
                      </Button>
                    </>
                  )}
                </div>

                {/* Info Box */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h4 className="font-medium text-blue-900 mb-2">
                    {t('Exam Format', 'Formato del Examen')}
                  </h4>
                  <ul className="text-sm text-blue-700 space-y-1">
                    <li>• 100 {t('multiple-choice questions', 'preguntas de opción múltiple')}</li>
                    <li>• {t('4 options per question', '4 opciones por pregunta')}</li>
                    <li>• {t('2 hours time limit', '2 horas de tiempo límite')}</li>
                    <li>• {t('80% minimum to pass', '80% mínimo para aprobar')}</li>
                    <li>• {t('Questions unique from module quizzes', 'Preguntas únicas (diferentes a los quizzes)')}</li>
                  </ul>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Results History Tab */}
          <TabsContent value="results" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <History className="h-5 w-5 text-purple-600" />
                  {t('Assessment Results History', 'Historial de Resultados')}
                </CardTitle>
                <CardDescription>
                  {t('View all online assessment results administered to participants.',
                     'Ver todos los resultados de evaluaciones en línea administradas a participantes.')}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {results.length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground">
                    <History className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>{t('No assessment results yet.', 'Aún no hay resultados de evaluaciones.')}</p>
                    <p className="text-sm mt-2">
                      {t('Results will appear here after administering online assessments.',
                         'Los resultados aparecerán aquí después de administrar evaluaciones en línea.')}
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {results.map((result) => (
                      <Card key={result.id} className={`border ${result.passed ? 'border-green-200 bg-green-50/50' : 'border-red-200 bg-red-50/50'}`}>
                        <CardContent className="p-4">
                          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-2">
                                {result.passed ? (
                                  <CheckCircle className="h-5 w-5 text-green-600" />
                                ) : (
                                  <XCircle className="h-5 w-5 text-red-600" />
                                )}
                                <span className="font-semibold text-lg">{result.participant_name}</span>
                                <Badge className={result.passed ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}>
                                  {result.passed ? t('PASSED', 'APROBADO') : t('FAILED', 'NO APROBADO')}
                                </Badge>
                              </div>
                              
                              <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
                                <span className="flex items-center gap-1">
                                  <BookOpen className="h-4 w-4" />
                                  {result.assessment_type === 'final' 
                                    ? t('Final Exam', 'Examen Final')
                                    : t('Module Quiz', 'Quiz de Módulo')}
                                </span>
                                <span className="flex items-center gap-1">
                                  <Trophy className="h-4 w-4" />
                                  <strong className={result.passed ? 'text-green-600' : 'text-red-600'}>
                                    {result.score}%
                                  </strong>
                                  ({result.correct_answers}/{result.total_questions})
                                </span>
                                <span className="flex items-center gap-1">
                                  <Clock className="h-4 w-4" />
                                  {result.time_taken_minutes} min
                                </span>
                                <span className="flex items-center gap-1">
                                  <Calendar className="h-4 w-4" />
                                  {formatDate(result.created_at)}
                                </span>
                              </div>
                            </div>
                            
                            <div className="flex gap-2">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleDownloadResult(result)}
                                disabled={downloading[`result-${result.id}`]}
                              >
                                {downloading[`result-${result.id}`] ? (
                                  <RefreshCw className="h-4 w-4 mr-1 animate-spin" />
                                ) : (
                                  <FileDown className="h-4 w-4 mr-1" />
                                )}
                                {t('Download PDF', 'Descargar PDF')}
                              </Button>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
